import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import { Image, SquarePen } from 'lucide-react';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import { AddToYarutaOption } from './AddToYarutaOption';

export interface AddToYarutaSheetProps {
  open: boolean;
  onClose(): void;
  /** The one file the parent picked (v1: one at a time). */
  onFile(file: File): void;
  /** Today's create form. */
  onTypeItIn(): void;
}

/**
 * What the picker offers. `file.type` decides nothing — the file is
 * classified by its bytes afterwards (lib/uploadPrep.ts); this only narrows
 * the OS picker. HEIC is listed so iPhone photos can be picked and
 * converted in the browser.
 */
const ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf';

/**
 * Add to Yaruta (OCR v1, YAR-556): the `.bottom sheet` Height=content
 * holding `Add to Yaruta / options` (Figma 11693:8423, placed in
 * "Add to Yaruta / sheet" 11697:8637). Take a photo is left out in v1
 * (web app; Meeting #167). One file, no `capture`.
 */
export function AddToYarutaSheet({
  open,
  onClose,
  onFile,
  onTypeItIn,
}: AddToYarutaSheetProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires a change.
    e.target.value = '';
    if (!file) return;
    onFile(file);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      title={ADD_TO_YARUTA_COPY.sheetTitle}
      closeLabel={ADD_TO_YARUTA_COPY.close}
      onClose={onClose}
      testId="add-to-yaruta-sheet"
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm leading-5 text-text-caption">
          {ADD_TO_YARUTA_COPY.sheetBody}
        </p>
        <div className="flex flex-col">
          <AddToYarutaOption
            icon={Image}
            label={ADD_TO_YARUTA_COPY.optionChoose.label}
            hint={ADD_TO_YARUTA_COPY.optionChoose.hint}
            onClick={() => inputRef.current?.click()}
          />
          <AddToYarutaOption
            icon={SquarePen}
            label={ADD_TO_YARUTA_COPY.optionType.label}
            hint={ADD_TO_YARUTA_COPY.optionType.hint}
            onClick={() => {
              onTypeItIn();
              onClose();
            }}
            divider
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={handleChange}
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="add-to-yaruta-file-input"
        />
      </div>
    </BottomSheet>
  );
}
